import type { CaseModel, StudyModel } from '@/models';
import type { TimepointOption } from '@/models/caseNote';
import { parseTimepointToMinutes } from '@/utils';

/**
 * Timepoints a case note can be tagged with, read off the case's existing entries for the field the PUMP data
 * dictionary designates as the timepoint. Returns an empty list for any study that isn't a PUMP study.
 */
export const getPumpTimepointOptions = (
  caseModel: CaseModel | undefined,
  selectedStudy: StudyModel | undefined
): TimepointOption[] => {
  if (!caseModel || selectedStudy?.flags?.customDataDictionary !== 'PUMP') {
    return [];
  }

  const dataObjectKey = `${selectedStudy?.flags?.customDataDictionary}_DATA`;
  //@ts-expect-error: dynamic data for PUMP
  const dataDictionary = selectedStudy?.flags?.[dataObjectKey] as Record<string, string> | undefined;
  if (!dataDictionary) {
    return [];
  }

  const firstMapping = Object.entries(dataDictionary)[0];
  if (!firstMapping) {
    return [];
  }

  const [formIdentifier, fieldIdentifier] = firstMapping;
  const entries = caseModel.entries?.[formIdentifier] ?? [];
  const values = new Set<string>();

  for (const entry of entries) {
    const rawValue = entry.items?.[fieldIdentifier];
    if (rawValue === undefined || rawValue === null) {
      continue;
    }

    const timepoint = `${rawValue}`.trim();
    if (timepoint) {
      values.add(timepoint);
    }
  }

  return Array.from(values)
    .sort((a, b) => {
      const aMinutes = parseTimepointToMinutes(a);
      const bMinutes = parseTimepointToMinutes(b);

      if (aMinutes !== bMinutes) {
        return aMinutes - bMinutes;
      }

      return a.localeCompare(b);
    })
    .map(value => ({
      label: value,
      value
    }));
};
